import { algoliasearch } from 'algoliasearch';
import type { SearchClient } from 'algoliasearch';
import axiosInstance from '@/api/axiosInstance';

export interface SearchConfig {
  securedApiKey: string;
  indices: {
    events: string;
    resources: string;
  };
  applicationId: string;
  userTradeIds: number[];
  expiresAt: number;
}

export interface SearchResult {
  objectID: string;
  eventId?: number;
  resourceId?: number;
  uuid?: string;
  slug?: string;
  title?: string;
  eventName?: string;
  eventDescription?: string;
  searchDescription?: string;
  eventPrice?: number;
  eventType?: string;
  eventCategory?: string;
  resourceType?: string;
  tags?: string[];
  categories?: string[];
  trades?: string[];
  url: string;
  thumbnail?: string;
  primaryIcon?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  viewCount?: number;
  sessions?: Array<{
    name: string;
    startDate: string;
    endDate: string;
    venue?: string;
    location?: string;
    isVirtual: boolean;
    instructor?: string;
  }>;
  _highlightResult?: any;
}

export interface SearchFilters {
  type?: 'all' | 'events' | 'resources';
  resourceType?: string;
  eventType?: string;
  eventCategory?: string;
  tags?: string[];
  categories?: string[];
  dateRange?: {
    start?: Date;
    end?: Date;
  };
}

export interface FacetValue {
  value: string;
  count: number;
  isRefined?: boolean;
}

export interface SearchFacets {
  resourceType?: Record<string, number>;
  eventType?: Record<string, number>;
  eventCategory?: Record<string, number>;
  tags?: Record<string, number>;
  categories?: Record<string, number>;
}

class SearchService {
  private searchClient: SearchClient | null = null;
  private config: SearchConfig | null = null;
  private configPromise: Promise<SearchConfig> | null = null;

  /**
   * Initialize the search service with secured configuration from the API
   * @param forceRefresh - Force refresh the configuration from the server
   */
  async initialize(forceRefresh: boolean = false): Promise<void> {
    if (forceRefresh || !this.configPromise) {
      // Force a refresh by calling the refresh endpoint
      if (forceRefresh) {
        this.configPromise = this.refreshConfig();
      } else {
        this.configPromise = this.fetchConfig();
      }
    }

    this.config = await this.configPromise;

    // Initialize Algolia client with secured key
    this.searchClient = algoliasearch(
      this.config.applicationId,
      this.config.securedApiKey
    );
  }

  /**
   * Force refresh the configuration from the API
   */
  private async refreshConfig(): Promise<SearchConfig> {
    const response = await axiosInstance.post<SearchConfig>('/api/search/refresh');
    return response.data;
  }

  /**
   * Fetch secured search configuration from the API
   */
  private async fetchConfig(): Promise<SearchConfig> {
    const response = await axiosInstance.get<SearchConfig>('/api/search/config');
    console.log('Fetched search config from API:', response.data);
    return response.data;
  }

  /**
   * Refresh the search configuration if expired
   */
  async refreshIfNeeded(): Promise<void> {
    if (!this.config) {
      await this.initialize();
      return;
    }

    // Check if the key is about to expire (within 5 minutes)
    const now = Math.floor(Date.now() / 1000);
    if (now >= this.config.expiresAt - 300) {
      const response = await axiosInstance.post<SearchConfig>('/api/search/refresh');
      this.config = response.data;

      // Reinitialize client with new key
      this.searchClient = algoliasearch(
        this.config.applicationId,
        this.config.securedApiKey
      );
    }
  }

  /**
   * Ensure the service is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.searchClient || !this.config) {
      await this.initialize();
    } else {
      await this.refreshIfNeeded();
    }
  }

  /**
   * Search across all indices
   */
  async searchAll(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20
  ): Promise<{
    events: SearchResult[];
    resources: SearchResult[];
    totalEvents: number;
    totalResources: number;
    facets: SearchFacets;
  }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error('Search service not initialized');
    }

    console.log('Search config:', this.config);
    console.log('Searching with query:', query);
    console.log('Filters:', filters);

    const queries = [];

    // Determine which indices to search
    if (!filters.type || filters.type === 'all' || filters.type === 'events') {
      const eventFilters = this.buildEventFilters(filters);
      queries.push({
        indexName: this.config.indices.events,
        query,
        page,
        hitsPerPage,
        // Request facets to get dynamic filter options
        facets: ['eventType', 'eventCategory', 'tags'],
        // Only add filters if there are any
        ...(eventFilters ? { filters: eventFilters } : {}),
      });
    }

    if (!filters.type || filters.type === 'all' || filters.type === 'resources') {
      const resourceFilters = this.buildResourceFilters(filters);
      queries.push({
        indexName: this.config.indices.resources,
        query,
        page,
        hitsPerPage,
        // Request facets to get dynamic filter options
        facets: ['resourceType', 'tags', 'categories'],
        // Only add filters if there are any
        ...(resourceFilters ? { filters: resourceFilters } : {}),
      });
    }

    console.log('Queries being sent to Algolia:', JSON.stringify(queries, null, 2));

    const results = await this.searchClient.search(queries);

    console.log('Raw Algolia response:', JSON.stringify(results, null, 2));
    console.log('Results array:', results.results);

    // Parse results based on what was searched
    let eventResults: any = { hits: [], nbHits: 0, facets: {} };
    let resourceResults: any = { hits: [], nbHits: 0, facets: {} };

    results.results.forEach((result: any) => {
      console.log('Processing result for index:', result.index);
      if (result.index === this.config?.indices.events) {
        eventResults = result;
      } else if (result.index === this.config?.indices.resources) {
        resourceResults = result;
      }
    });

    console.log('Parsed event results:', eventResults);
    console.log('Parsed resource results:', resourceResults);

    // Combine facets from both indices
    const combinedFacets: SearchFacets = {};

    if (eventResults.facets) {
      if (eventResults.facets.eventType) {
        combinedFacets.eventType = eventResults.facets.eventType;
      }
      if (eventResults.facets.eventCategory) {
        combinedFacets.eventCategory = eventResults.facets.eventCategory;
      }
      if (eventResults.facets.tags) {
        // Merge tags from events
        combinedFacets.tags = { ...(combinedFacets.tags || {}), ...eventResults.facets.tags };
      }
    }

    if (resourceResults.facets) {
      if (resourceResults.facets.resourceType) {
        combinedFacets.resourceType = resourceResults.facets.resourceType;
      }
      if (resourceResults.facets.categories) {
        combinedFacets.categories = resourceResults.facets.categories;
      }
      if (resourceResults.facets.tags) {
        // Merge tags from resources
        combinedFacets.tags = { ...(combinedFacets.tags || {}), ...resourceResults.facets.tags };
      }
    }

    console.log('Combined facets:', combinedFacets);

    return {
      events: eventResults.hits as SearchResult[],
      resources: resourceResults.hits as SearchResult[],
      totalEvents: eventResults.nbHits,
      totalResources: resourceResults.nbHits,
      facets: combinedFacets,
    };
  }

  /**
   * Search only events
   */
  async searchEvents(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20
  ): Promise<{ hits: SearchResult[]; total: number }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error('Search service not initialized');
    }

    const result = await this.searchClient.searchSingleIndex({
      indexName: this.config.indices.events,
      searchParams: {
        query,
        page,
        hitsPerPage,
        filters: this.buildEventFilters(filters),
      },
    });

    return {
      hits: result.hits as SearchResult[],
      total: result.nbHits || 0,
    };
  }

  /**
   * Search only resources
   */
  async searchResources(
    query: string,
    filters: SearchFilters = {},
    page: number = 0,
    hitsPerPage: number = 20
  ): Promise<{ hits: SearchResult[]; total: number }> {
    await this.ensureInitialized();

    if (!this.searchClient || !this.config) {
      throw new Error('Search service not initialized');
    }

    const result = await this.searchClient.searchSingleIndex({
      indexName: this.config.indices.resources,
      searchParams: {
        query,
        page,
        hitsPerPage,
        filters: this.buildResourceFilters(filters),
      },
    });

    return {
      hits: result.hits as SearchResult[],
      total: result.nbHits || 0,
    };
  }

  /**
   * Get search suggestions for autocomplete
   */
  async getSuggestions(query: string, limit: number = 5): Promise<SearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const results = await this.searchAll(query, {}, 0, limit);

    // Combine and sort by relevance
    const combined = [
      ...results.events.slice(0, Math.ceil(limit / 2)),
      ...results.resources.slice(0, Math.floor(limit / 2)),
    ];

    return combined.slice(0, limit);
  }

  /**
   * Build filter string for events
   */
  private buildEventFilters(filters: SearchFilters): string {
    const filterParts: string[] = [];

    if (filters.eventType) {
      filterParts.push(`eventType:"${filters.eventType}"`);
    }

    if (filters.eventCategory) {
      filterParts.push(`eventCategory:"${filters.eventCategory}"`);
    }

    if (filters.tags && filters.tags.length > 0) {
      const tagFilters = filters.tags.map(tag => `tags:"${tag}"`).join(' OR ');
      filterParts.push(`(${tagFilters})`);
    }

    return filterParts.join(' AND ');
  }

  /**
   * Build filter string for resources
   */
  private buildResourceFilters(filters: SearchFilters): string {
    const filterParts: string[] = [];

    if (filters.resourceType) {
      filterParts.push(`resourceType:"${filters.resourceType}"`);
    }

    if (filters.categories && filters.categories.length > 0) {
      const categoryFilters = filters.categories
        .map(cat => `categories:"${cat}"`)
        .join(' OR ');
      filterParts.push(`(${categoryFilters})`);
    }

    if (filters.tags && filters.tags.length > 0) {
      const tagFilters = filters.tags.map(tag => `tags:"${tag}"`).join(' OR ');
      filterParts.push(`(${tagFilters})`);
    }

    return filterParts.join(' AND ');
  }

  /**
   * Get the current user's trade IDs (for display purposes)
   */
  getUserTradeIds(): number[] {
    return this.config?.userTradeIds || [];
  }
}

// Export singleton instance
export const searchService = new SearchService();